import React from "react";
import { Box, IconButton, Menu, MenuItem, Snackbar, Alert, CircularProgress, Badge } from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import PillSearchField from "../../../components/PillSearchField";
import ApplicationListItem from "./ApplicationListItem";
import ApplicationDetailPane from "./ApplicationDetailPane";
import { fetchTherapistApplications } from "../../../Services/therapistApplicationsService";

const STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "revoked", label: "Revoked" },
];

export default function TherapistApplicationsTab() {
  const [applications, setApplications] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [search, setSearch] = React.useState("");
  // Admins land on the queue they actually need to work — pending — rather
  // than an undifferentiated "All" list.
  const [statusFilter, setStatusFilter] = React.useState("pending");
  const [filterAnchorEl, setFilterAnchorEl] = React.useState(null);
  const [selectedId, setSelectedId] = React.useState(null);
  const [snackbar, setSnackbar] = React.useState({ open: false, message: "", severity: "success" });
  const [page, setPage] = React.useState(1);
  const [totalPages, setTotalPages] = React.useState(1);
  const [total, setTotal] = React.useState(0);

  const isMountedRef = React.useRef(true);
  React.useEffect(() => () => (isMountedRef.current = false), []);

  const notify = (message, severity = "success") => setSnackbar({ open: true, message, severity });

  React.useEffect(() => {
    let isMounted = true;
    const handle = setTimeout(async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await fetchTherapistApplications({ status: statusFilter, search, page: 1, limit: 20 });
        if (isMounted) {
          const list = res?.data?.applications || [];
          setApplications(list);
          setPage(res?.data?.page || 1);
          setTotalPages(res?.data?.totalPages || 1);
          setTotal(res?.data?.total ?? list.length);
          setSelectedId((prev) => (list.some((a) => a.id === prev) ? prev : list[0]?.id ?? null));
        }
      } catch (err) {
        if (isMounted) {
          setError(err.message || "Failed to load applications");
          setApplications([]);
          setPage(1);
          setTotalPages(1);
          setTotal(0);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    }, 300);

    return () => {
      isMounted = false;
      clearTimeout(handle);
    };
  }, [statusFilter, search]);

  const loadMore = React.useCallback(async () => {
    if (loading || loadingMore || page >= totalPages) return;
    try {
      setLoadingMore(true);
      const nextPage = page + 1;
      const res = await fetchTherapistApplications({ status: statusFilter, search, page: nextPage, limit: 20 });
      if (isMountedRef.current) {
        const list = res?.data?.applications || [];
        setApplications((prev) => [...prev, ...list]);
        setPage(res?.data?.page || nextPage);
        setTotalPages(res?.data?.totalPages || totalPages);
        setTotal(res?.data?.total ?? total);
      }
    } catch (err) {
      if (isMountedRef.current) notify(err.message || "Failed to load more applications", "error");
    } finally {
      if (isMountedRef.current) setLoadingMore(false);
    }
  }, [loading, loadingMore, page, totalPages, statusFilter, search, total]);

  const handleListScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 150) loadMore();
  };

  const selectedApplication = applications.find((a) => a.id === selectedId) || null;

  const handleStatusChange = (id, patch) => {
    setApplications((prev) => prev.map((a) => (a.id === id ? { ...a, ...patch } : a)));
  };

  const activeStatusOption = STATUS_OPTIONS.find((o) => o.value === statusFilter);

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        width: "100%",
        backgroundColor: "#FFFFFF",
        border: "1px solid #e5e7eb",
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      {/* Left: search + filter + list */}
      <Box
        sx={{
          width: 380,
          minWidth: 380,
          flexShrink: 0,
          borderRight: "1px solid #e5e7eb",
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          bgcolor: "#F9FAFB",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 1.5, flexShrink: 0 }}>
          <Box sx={{ flex: 1 }}>
            <PillSearchField
              value={search}
              onChange={setSearch}
              placeholder="Search name, email, city…"
              resultCount={total}
              loading={loading}
            />
          </Box>
          <Badge color="primary" variant="dot" invisible={statusFilter === "all"}>
            <IconButton
              onClick={(e) => setFilterAnchorEl(e.currentTarget)}
              aria-label="Filter by status"
              sx={{ bgcolor: "#FFFFFF", border: "1px solid #e5e7eb", "&:hover": { bgcolor: "#F3F4F6" } }}
            >
              <FilterListIcon fontSize="small" />
            </IconButton>
          </Badge>
          <Menu anchorEl={filterAnchorEl} open={Boolean(filterAnchorEl)} onClose={() => setFilterAnchorEl(null)}>
            {STATUS_OPTIONS.map((opt) => (
              <MenuItem
                key={opt.value}
                selected={opt.value === statusFilter}
                onClick={() => {
                  setStatusFilter(opt.value);
                  setFilterAnchorEl(null);
                }}
              >
                {opt.label}
              </MenuItem>
            ))}
          </Menu>
        </Box>

        <Box sx={{ px: 1.75, pb: 1, flexShrink: 0 }}>
          <Box sx={{ fontSize: "0.6875rem", fontWeight: 600, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.04em" }}>
            {activeStatusOption?.label} · {total}
          </Box>
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", pb: 1 }} onScroll={handleListScroll}>
          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={22} />
            </Box>
          )}
          {!loading && error && (
            <Box sx={{ px: 2, py: 2 }}>
              <Alert severity="error">{error}</Alert>
            </Box>
          )}
          {!loading && !error && applications.length === 0 && (
            <Box sx={{ px: 2, py: 3, textAlign: "center", color: "#9ca3af", fontSize: "0.8125rem" }}>
              No applications found.
            </Box>
          )}
          {!loading &&
            !error &&
            applications.map((application) => (
              <ApplicationListItem
                key={application.id}
                application={application}
                selected={application.id === selectedId}
                onClick={() => setSelectedId(application.id)}
              />
            ))}
          {!loading && loadingMore && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={18} />
            </Box>
          )}
        </Box>
      </Box>

      {/* Right: detail pane */}
      <ApplicationDetailPane application={selectedApplication} onStatusChange={handleStatusChange} onNotify={notify} />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} variant="filled" sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
